// AFF-spec event logging for Fyrefly.
//
// Mirrors the USDA Forest Service Automated Flight Following JSON spec
// (Sept 2016, v1.0). Every event the app generates today goes through
// this helper so the wire format already matches what a future
// Spidertracks / TracPlus / Skytrac webhook would deliver.
//
// Two write paths:
//   1. logAffEvent({...})       — full struct (used by integrations)
//   2. logManualDrop / logFill  — convenience wrappers for the pilot UI
//
// All writes also emit a `flight.aff_event` audit event so the Tier 1
// compliance log records what happened.

import { supabase } from './supabase';
import { trackAuditEvent } from './audit';
import { useAccountContextStore } from '../stores/accountContextStore';
import { useGpsStore } from '../stores/gpsStore';
import { newClientId } from './ids';
import { enqueueWrite, registerReplayHandler, type QueueEntry } from './writeQueue';

// ── Write-queue replay handler ─────────────────────────────────────────────
// Registered at module load. drainQueue() calls this when connectivity
// returns to replay any events that failed to reach Supabase offline.
registerReplayHandler('aff_event', async (entry: QueueEntry) => {
  const result = await logAffEvent(entry.payload as AffEventInput);
  // 'supabase not configured' and 'not signed in' are transient at
  // drain time — leave in queue for next drain. True network errors
  // should retry. 23505 dedup means it already landed, treat as success.
  if (!result.ok && result.error === 'supabase not configured') return false;
  if (!result.ok && result.error === 'not signed in') return false;
  return result.ok || result.error?.includes('23505') || false;
});

/**
 * AFF event kinds. Mirrors `aff_events.kind` in migration 0028 and the
 * AFF spec Appendix 2 ATU event taxonomy.
 */
export type AffEventKind =
  | 'position'
  | 'heartbeat'
  | 'power_on'  | 'power_off'
  | 'oil_press_on' | 'oil_press_off'
  | 'status_takeoff' | 'status_landing' | 'status_inflight'
  | 'wow_on'  | 'wow_off'
  | 'gear_up' | 'gear_down'
  | 'fill_start' | 'fill_end'
  | 'pump_start' | 'pump_end'
  | 'drop_start' | 'drop_end'
  | 'firmware_update'
  | 'calibration';

export type AffSubject = 'aircraft' | 'tank' | 'bucket' | 'hardware';

export type AffSuppressant = 'retardant' | 'foam' | 'water' | 'gel' | 'ukn';

export type AffVendor =
  | 'fyrefly-app'
  | 'spidertracks'
  | 'tracplus'
  | 'skytrac'
  | 'latitude'
  | 'flightcell-dzmx'
  | 'blue-sky'
  | 'aff-direct';

export interface AffEventInput {
  kind: AffEventKind;
  /** Defaults to 'aircraft' for position/status events; should be
   *  'tank' or 'bucket' for drop/fill events. */
  subject?: AffSubject;

  /** Client-generated idempotency key. When the caller wants to retry
   *  safely on network failure, pass the SAME clientId on every retry.
   *  Migration 0035 added a partial unique index on (owner_id,
   *  client_id); a retry with the same key returns 23505 unique
   *  violation and we reconcile by SELECT-ing the existing row.
   *  When omitted, a fresh id is generated per call (no idempotency).
   *  Boss applied migration 2026-05-13. */
  clientId?: string;

  /** When the GPS fix that goes with this event was sampled. Defaults
   *  to now. Should be the actual fix time when available. */
  posTime?: Date;

  /** Cross-references. */
  flightId?: string | null;
  flightClientId?: string | null;
  aircraftClientId?: string | null;
  incidentId?: string | null;

  /** Position fields (AFF canonical units). When omitted, the helper
   *  fills them from the current GPS fix if available. */
  lat?: number;
  lon?: number;
  altM?: number;          // meters MSL
  speedMps?: number;      // meters per second
  headingTrue?: number;   // degrees True
  fixType?: '3D' | '2D' | 'invalid';
  pdop?: number;
  hdop?: number;
  vdop?: number;
  tdop?: number;
  posSrc?: 'GPS' | 'GLONAS' | 'Gallileo' | 'loran';

  /** Drop/fill state. */
  suppressant?: AffSuppressant;
  gallons?: number;
  liters?: number;
  coverageLvl?: number;

  /** Provenance. Default 'fyrefly-app'. */
  vendor?: AffVendor;
  esn?: string;
  unitId?: string;
  vendorMsgId?: string;
  rawPayload?: Record<string, unknown>;

  notes?: string;
}

export interface AffEventResult {
  ok: boolean;
  id?: string;
  error?: string;
}

/**
 * Convert m/s ↔ knots. Provided as helpers so UI code doesn't sprinkle
 * conversion math; DB always speaks the AFF canonical (m/s).
 */
export function knotsToMps(knots: number): number { return knots * 0.5144444; }
export function mpsToKnots(mps: number): number { return mps / 0.5144444; }

/** Convert ft ↔ m for altitude. DB speaks meters MSL (AFF canonical). */
export function ftToM(ft: number): number { return ft * 0.3048; }
export function mToFt(m: number): number { return m / 0.3048; }

/**
 * Insert a single AFF event. Fire-and-forget audit emit on success.
 *
 * If position fields are omitted, fills them from the current GPS fix.
 * That makes the call site for a "drop_start" event as terse as:
 *   logAffEvent({ kind: 'drop_start', subject: 'tank', suppressant: 'retardant', gallons: 800 })
 * and the position is captured automatically.
 */
export async function logAffEvent(input: AffEventInput): Promise<AffEventResult> {
  if (!supabase) {
    return { ok: false, error: 'supabase not configured' };
  }

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    return { ok: false, error: 'not signed in' };
  }
  const ownerId = user.id;

  const ctx = useAccountContextStore.getState().ctx;
  const orgId = ctx.kind === 'organizational' ? ctx.orgId : null;

  // Auto-fill position from the live GPS fix when the caller didn't
  // specify one. This is the common case for in-app manual events.
  const gpsFix = useGpsStore.getState().fix;
  const lat         = input.lat         ?? gpsFix?.lat ?? null;
  const lon         = input.lon         ?? gpsFix?.lon ?? null;
  const altM        = input.altM        ?? gpsFix?.altMeters ?? null;
  const speedMps    = input.speedMps    ?? gpsFix?.speedMps ?? null;
  const headingTrue = input.headingTrue ?? (gpsFix?.headingDeg != null ? Math.round(gpsFix.headingDeg) : null);

  const posTime = input.posTime ?? (gpsFix?.timestamp ? new Date(gpsFix.timestamp) : new Date());

  const row = {
    owner_id:           ownerId,
    org_id:             orgId,
    // input.flightId is, by convention across this codebase, the
    // local client_id (text) of the flight — not a server uuid. The
    // legacy uuid column is kept null to dodge the silent-fail bug
    // that bit aircraft/crew assignments. New writes go through the
    // denormalized text column with a composite FK to flights
    // (owner_id, client_id). See migration 0031 + 0032.
    flight_id:          null,
    flight_client_id:   input.flightClientId ?? input.flightId ?? null,
    aircraft_client_id: input.aircraftClientId ?? null,
    // incidentId is the local client_id (text), not a server uuid.
    // Use the denormalized text column added in migration 0031.
    incident_id:        null,
    incident_client_id: input.incidentId ?? null,

    kind:    input.kind,
    subject: input.subject ?? 'aircraft',

    pos_time:     posTime.toISOString(),
    lat,
    lon,
    alt_m:        altM,
    speed_mps:    speedMps,
    heading_true: headingTrue,
    fix_type:     input.fixType  ?? (gpsFix ? '3D' : null),
    pdop:         input.pdop     ?? null,
    hdop:         input.hdop     ?? null,
    vdop:         input.vdop     ?? null,
    tdop:         input.tdop     ?? null,
    pos_src:      input.posSrc   ?? (gpsFix ? 'GPS' : null),

    suppressant:  input.suppressant ?? null,
    gallons:      input.gallons     ?? null,
    liters:       input.liters      ?? null,
    coverage_lvl: input.coverageLvl ?? null,

    vendor:        input.vendor    ?? 'fyrefly-app',
    esn:           input.esn       ?? null,
    unit_id:       input.unitId    ?? null,
    vendor_msg_id: input.vendorMsgId ?? null,
    raw_payload:   input.rawPayload  ?? null,

    // Idempotency key for client-originated events. Caller passes a
    // stable key on retry; the partial unique index on (owner_id,
    // client_id) where client_id IS NOT NULL bounces duplicates.
    // External vendor-ingested rows use vendor_msg_id instead and
    // leave client_id null.
    client_id: input.clientId ?? newClientId('aff'),

    notes:         input.notes ?? null,
  };

  let { data, error } = await supabase
    .schema('app')
    .from('aff_events')
    .insert(row)
    .select('id')
    .single();

  // Reconcile on unique-violation: server already has this row, look
  // it up and return its id so the caller's audit trail is correct.
  if (error && (error as { code?: string }).code === '23505') {
    const lookup = await supabase
      .schema('app')
      .from('aff_events')
      .select('id')
      .eq('owner_id', ownerId)
      .eq('client_id', row.client_id)
      .maybeSingle();
    if (lookup.data) {
      // eslint-disable-next-line no-console
      console.warn('[affEvents] reconciled via client_id unique violation:', row.client_id);
      data = lookup.data as typeof data;
      error = null;
    }
  }

  if (error) {
    // eslint-disable-next-line no-console
    console.warn('[affEvents] insert failed:', error.message);
    // Write-queue: persist to IDB so the event survives offline and is
    // replayed automatically when connectivity returns (OAC5).
    // The client_id idempotency key makes replay safe.
    void enqueueWrite({
      id: row.client_id as string,
      type: 'aff_event',
      enqueuedAt: new Date().toISOString(),
      payload: input,
    });
    return { ok: false, error: error.message };
  }

  const newId = (data as { id: string } | null)?.id;

  // Fire-and-forget audit emit so the Tier 1 log captures the action.
  trackAuditEvent({
    kind: 'flight.aff_event',
    subjectKind: 'flight',
    subjectId: input.flightId ?? input.flightClientId ?? undefined,
    payload: {
      affEventId: newId,
      affKind: input.kind,
      subject: input.subject ?? 'aircraft',
      vendor: input.vendor ?? 'fyrefly-app',
      suppressant: input.suppressant ?? null,
      gallons: input.gallons ?? null,
      incidentId: input.incidentId ?? null,
    },
  });

  return { ok: true, id: newId };
}

/**
 * Convenience: log a drop_start event for a fixed-wing tank or
 * helicopter bucket. Auto-fills position from the live GPS fix.
 */
export async function logDropStart(opts: {
  suppressant: AffSuppressant;
  gallons?: number;
  liters?: number;
  coverageLvl?: number;
  subject?: 'tank' | 'bucket';
  flightId?: string;
  incidentId?: string;
  notes?: string;
  /** Optional idempotency key forwarded to logAffEvent. See AffEventInput.clientId. */
  clientId?: string;
}): Promise<AffEventResult> {
  return logAffEvent({
    kind: 'drop_start',
    subject: opts.subject ?? 'tank',
    suppressant: opts.suppressant,
    gallons: opts.gallons,
    liters: opts.liters,
    coverageLvl: opts.coverageLvl,
    flightId: opts.flightId,
    incidentId: opts.incidentId,
    notes: opts.notes,
    clientId: opts.clientId,
  });
}

/** Companion: log drop_end. */
export async function logDropEnd(opts: {
  subject?: 'tank' | 'bucket';
  flightId?: string;
  incidentId?: string;
  notes?: string;
  clientId?: string;
}): Promise<AffEventResult> {
  return logAffEvent({
    kind: 'drop_end',
    subject: opts.subject ?? 'tank',
    flightId: opts.flightId,
    incidentId: opts.incidentId,
    notes: opts.notes,
    clientId: opts.clientId,
  });
}

/** Log a fill_start (loading suppressant) event. */
export async function logFillStart(opts: {
  suppressant: AffSuppressant;
  gallons?: number;
  liters?: number;
  subject?: 'tank' | 'bucket';
  flightId?: string;
  incidentId?: string;
  notes?: string;
  clientId?: string;
}): Promise<AffEventResult> {
  return logAffEvent({
    kind: 'fill_start',
    subject: opts.subject ?? 'tank',
    suppressant: opts.suppressant,
    gallons: opts.gallons,
    liters: opts.liters,
    flightId: opts.flightId,
    incidentId: opts.incidentId,
    notes: opts.notes,
    clientId: opts.clientId,
  });
}

export async function logFillEnd(opts: {
  subject?: 'tank' | 'bucket';
  flightId?: string;
  incidentId?: string;
  notes?: string;
  clientId?: string;
}): Promise<AffEventResult> {
  return logAffEvent({
    kind: 'fill_end',
    subject: opts.subject ?? 'tank',
    flightId: opts.flightId,
    incidentId: opts.incidentId,
    notes: opts.notes,
    clientId: opts.clientId,
  });
}

/**
 * List recent AFF events for a flight (most recent first).
 */
export async function listAffEventsForFlight(flightId: string): Promise<Array<{
  id: string;
  kind: AffEventKind;
  subject: AffSubject;
  posTime: string;
  lat: number | null;
  lon: number | null;
  altM: number | null;
  suppressant: AffSuppressant | null;
  gallons: number | null;
  coverageLvl: number | null;
  vendor: AffVendor;
  notes: string | null;
}>> {
  if (!supabase) return [];
  const { data, error } = await supabase
    .schema('app')
    .from('aff_events')
    .select('id, kind, subject, pos_time, lat, lon, alt_m, suppressant, gallons, coverage_lvl, vendor, notes')
    .eq('flight_id', flightId)
    .order('pos_time', { ascending: false });
  if (error) {
    // eslint-disable-next-line no-console
    console.warn('[affEvents] list failed:', error.message);
    return [];
  }
  return (data ?? []).map((r) => ({
    id:           r.id as string,
    kind:         r.kind as AffEventKind,
    subject:      r.subject as AffSubject,
    posTime:      r.pos_time as string,
    lat:          (r.lat ?? null) as number | null,
    lon:          (r.lon ?? null) as number | null,
    altM:         (r.alt_m ?? null) as number | null,
    suppressant:  (r.suppressant ?? null) as AffSuppressant | null,
    gallons:      (r.gallons ?? null) as number | null,
    coverageLvl:  (r.coverage_lvl ?? null) as number | null,
    vendor:       r.vendor as AffVendor,
    notes:        (r.notes ?? null) as string | null,
  }));
}

/**
 * Rollup totals for an incident — useful for incident billing +
 * post-incident reporting. Counts drops and sums suppressant by type.
 */
export async function rollupAffEventsForIncident(incidentId: string): Promise<{
  drops: number;
  gallonsByType: Record<AffSuppressant, number>;
}> {
  if (!supabase) return { drops: 0, gallonsByType: {} as Record<AffSuppressant, number> };
  const { data, error } = await supabase
    .schema('app')
    .from('aff_events')
    .select('kind, suppressant, gallons')
    // Filter on the denormalized client-id column (incidentId passed
    // in is the local client_id, not a server uuid). See migration 0031.
    .eq('incident_client_id', incidentId)
    .eq('kind', 'drop_start');
  if (error) {
    // eslint-disable-next-line no-console
    console.warn('[affEvents] rollup failed:', error.message);
    return { drops: 0, gallonsByType: {} as Record<AffSuppressant, number> };
  }
  const rows = (data ?? []) as Array<{ suppressant: AffSuppressant | null; gallons: number | null }>;
  const out: Record<string, number> = {};
  let drops = 0;
  for (const r of rows) {
    drops += 1;
    const k = r.suppressant ?? 'ukn';
    out[k] = (out[k] ?? 0) + (r.gallons ?? 0);
  }
  return { drops, gallonsByType: out as Record<AffSuppressant, number> };
}
